import React, { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { complete_payment_method_update } from '../Services/Student/PaymentApi';
import './StripeOffcanvas.css';

const StripeOffcanvas = ({ isOpen, onClose, plan, mode = 'payment' }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [closing, setClosing] = useState(false);

  const formattedPrice = plan?.price != null ? Number(plan.price).toFixed(2) : null;

  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      setError(null);
      setSucceeded(false);
      setProcessing(false);
      setClosing(false);
    }
  }, [isOpen]);

  const handleSubmit = async ev => {
    ev.preventDefault();
    setProcessing(true);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      setProcessing(false);
      return;
    }

    let confirmResult;
    const confirmParams = {
      return_url: `${window.location.origin}/subscription-success`,
    };

    // This block handles updating a payment method.
    if (mode === 'setup') {
      // We prevent redirection to handle the API call to our backend here.
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (setupError) {
        setError(setupError.message);
      } else if (setupIntent && setupIntent.status === 'succeeded') {
        try {
          // Call the backend to finalize the update
          const apiResponse = await complete_payment_method_update(setupIntent.id);
          if (apiResponse && !apiResponse.code?.includes('ERR')) {
            setSucceeded(true);
            // Close the canvas after a short delay to show success
            setTimeout(() => onClose(), 2000);
          } else {
            setError(apiResponse?.message || 'Failed to update payment method on the server.');
          }
        } catch (apiError) {
          setError('An error occurred while finalizing the update. Please contact support.');
        }
      }
    } else if (mode === 'payment') {
      // ✅ Technically Correct: Use confirmPayment for PaymentIntent secrets
      // This is used for subscriptions requiring an immediate charge.
      // This flow will redirect the user to the success page.
      confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams,
      });
    } else {
      // Handle unexpected mode
      setError('Invalid payment flow mode detected. Please contact support.');
      setProcessing(false);
      return;
    }

    // This part is only for the 'payment' flow, as 'setup' is handled above.
    if (confirmResult) {
      const { error: confirmError } = confirmResult;

      if (confirmError) {
        if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
          setError(confirmError.message);
        } else {
          setError('An unexpected error occurred during payment confirmation.');
        }
      }
      // If confirmError is null, Stripe handles the redirection.
    }

    setProcessing(false);
  };

  return (
    <div className={`stripe-offcanvas ${isOpen ? 'open' : ''}`}>
      <div className="stripe-offcanvas-header">
        <h3>Complete Your Purchase</h3>
        <button
          onClick={async () => {
            setClosing(true);
            await onClose(); // This will now trigger the fetchStatus in the parent
          }}
          className="close-btn"
          disabled={closing}
        >
          &times;
        </button>
      </div>
      <div className="stripe-offcanvas-body">
        {closing ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '15px' }}>Please wait...</p>
          </div>
        ) : succeeded ? (
          <div className="payment-success">
            <h4>{mode === 'setup' ? 'Payment Method Updated!' : 'Payment Successful!'}</h4>
            <p>{mode === 'setup' ? 'Your new card has been saved.' : `Thank you for subscribing to the ${plan?.name} plan.`}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {formattedPrice && <>{mode === 'payment' ? <h4>Amount Due Today: ${formattedPrice}</h4> : <h4>{plan?.name}</h4>}</>}
            <PaymentElement id="payment-element" />
            <button disabled={processing || !stripe || !elements} className="pay-button" type="submit">
              {processing ? 'Processing...' : mode === 'setup' ? 'Save Card' : `Pay $${formattedPrice}`}
            </button>
            {error && (
              <div className="card-error" role="alert">
                {error}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default StripeOffcanvas;
